import React from "react";
import { Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  formatDate,
  getPriorDate,
  joinCounties,
  underCodesLimit,
  underNpiLimit,
  validateFileName,
  validateNpis
} from "../utils/commonFunctions";
import { underZipLimit, validateZipCodes } from "util/zip";
import { CustomMenu, CustomMenuCounties } from "./CustomMenu";
import CustomToggle from "./CustomToggle";
import cbsa from "util/cbsa";
import states from "util/states";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { readableRefDate } from "util/medicareRefDate";
import counties from "util/counties";
import {
  clinicalMarketShareIDs,
  populationTrackerIDs,
  shareOfCareIDs,
  countyLevelServicesIDs,
  clinicalVolumesIDs
} from 'features/queryReports/utils/reportIds';

export function FileNameInput({ fileName, setFileName, priorQueryParams }) {
  const isEmpty = !fileName;
  const isInvalid = fileName && !validateFileName(fileName);
  const isWarning =
    priorQueryParams && fileName?.trim() === priorQueryParams.description?.trim();

  return (
    <div className="form-group row">
      <OverlayTrigger
        placement="left"
        overlay={<Tooltip>The file name for your query results</Tooltip>}
      >
        <label className="col-3 col-form-label">File Name</label>
      </OverlayTrigger>

      <div className="col-6 pl-0 pr-0">
        <input
          type="text"
          className={`form-control ${isEmpty
            ? "is-invalid"
            : isInvalid
              ? "is-invalid"
              : isWarning
                ? "is-warning"
                : ""
            }`}
          value={fileName}
          onChange={e => setFileName(e.target.value)}
          placeholder="Name the query result file"
        />

        {isEmpty && <div className="invalid-feedback">May not be empty</div>}

        {fileName && isInvalid && (
          <div className="invalid-feedback">Must be valid file name</div>
        )}

        {isWarning && (
          <div
            className="invalid-feedback text-orange"
            style={{ display: "block" }}
          >
            Rename the file before resubmitting the query.
          </div>
        )}
      </div>
    </div>
  );
}

export function ProviderTypeSelect({ bind, label, overlayText }) {
  return (
    <div className="form-group row">
      <OverlayTrigger
        key="providertype"
        placement="left"
        overlay={
          <Tooltip>
            {overlayText}
          </Tooltip>
        }
      >
        <label htmlFor="providerType" className="col-3 col-form-label">
          {label}
        </label>
      </OverlayTrigger>
      <select className="form-control col-6" {...bind.select("providerType")}>
        <option value="">Select Type</option>
        <option value="physician">by Physician</option>
        <option value="organization">by Organization</option>
      </select>
    </div>
  );
}

export function ReportTypeSelect({ bind, label, overlayText, categoryId }) {
  return (
    <div className="form-group row">
      <OverlayTrigger
        key="reporttype"
        placement="left"
        overlay={
          <Tooltip>
            {overlayText}
          </Tooltip>
        }
      >
        <label htmlFor="reportType" className="col-3 col-form-label">
          {label}
        </label>
      </OverlayTrigger>
      <select className="form-control col-6" {...bind.select("reportType")}>
        <option value="">Select Type</option>
        {categoryId === clinicalMarketShareIDs.categoryId && <option value="procedure">CPT/HCPCS</option>}
        {categoryId === countyLevelServicesIDs.categoryId && <option value="cpt/hcpcs">CPT/HCPCS</option>}
        <option value="primaryDiagnosis">Primary Diagnosis</option>
        <option value="msdrg">MSDRG</option>
      </select>
    </div>
  );
}

export const RefDateSelect = ({
  id,
  label,
  value,
  onChange,
  placeholder = "Select Reference Date",
  className = "form-group row",
  selectClassName = "form-control col-6",
  overlayText
}) => {
  return (
    <div className={className}>
      <OverlayTrigger
        placement="left"
        overlay={<Tooltip>{overlayText || label}</Tooltip>}
      >
        <label htmlFor={id} className="col-3 col-form-label">
          {label}
        </label>
      </OverlayTrigger>

      <select
        id={id}
        className={selectClassName}
        value={value || ""}
        onChange={onChange}
      >
        <option value="">{placeholder}</option>
        <option value="2021q2">{readableRefDate("2021q2")}</option>
        <option value="2021q3">{readableRefDate("2021q3")}</option>
        <option value="2021q4">{readableRefDate("2021q4")}</option>
        <option value="2022q1">{readableRefDate("2022q1")}</option>
        <option value="2022q2">{readableRefDate("2022q2")}</option>
        <option value="2022q3">{readableRefDate("2022q3")}</option>
        <option value="2022q4">{readableRefDate("2022q4")}</option>
        <option value="2023q1">{readableRefDate("2023q1")}</option>
        <option value="2023q2">{readableRefDate("2023q2")}</option>
        <option value="2023q3">{readableRefDate("2023q3")}</option>
        <option value="2023q4">{readableRefDate("2023q4")}</option>
        <option value="2024q1">{readableRefDate("2024q1")}</option>
        <option value="2024q2">{readableRefDate("2024q2")}</option>
        <option value="2024q3">{readableRefDate("2024q3")}</option>
        <option value="2024q4">{readableRefDate("2024q4")}</option>
        <option value="2025q1">{readableRefDate("2025q1")}</option>
        <option value="2025q2">{readableRefDate("2025q2")}</option>
        <option value="2025q3">{readableRefDate("2025q3")}</option>
        <option value="2025q4">{readableRefDate("2025q4")}</option>
        <option value="2026q1">{readableRefDate("2026q1")}</option>
      </select>
    </div>
  );
};

export function GeographyTypeSelect({ common, bind, overlayText, allowedOptions }) {
  return (
    <div className="form-group row">
      <OverlayTrigger
        key="geographyType"
        placement="left"
        overlay={<Tooltip>{overlayText ? overlayText : "Geographic type used to define your market"}</Tooltip>}
      >
        <label htmlFor="providerType" className="col-3 col-form-label">
          Locations
        </label>
      </OverlayTrigger>
      <select className="form-control col-6" {...bind.select("geographyType")}>
        <option value="">Select Geography Type</option>
        {allowedOptions?.zipcode && (
          <option value="zipcode">Provider ZIP Codes</option>
        )}
        {allowedOptions?.cbsa && (
          <option value="cbsa">Provider CBSA</option>
        )}
        {allowedOptions?.state && (
          <option value="state">Provider State</option>
        )}
        {allowedOptions?.patientZip3 && (
          <option value="patientZip3">Patient ZIP3 Codes</option>
        )}
      </select>
    </div>
  );
}

export function ZipCodeInput({ common, bind }) {
  return (
    <div className="form-group row">
      <OverlayTrigger
        key="zipCodes"
        placement="left"
        overlay={
          <Tooltip>
            Provide a comma separated list of five digit zip codes to define
            your market
          </Tooltip>
        }
      >
        <label htmlFor="locations" className="col-3 col-form-label">
          ZIP codes
        </label>
      </OverlayTrigger>
      <textarea
        className={`form-control col-6 ${!validateZipCodes(common.zipCodes, ["zip", "zip+4"]) ||
          !underZipLimit(common.zipCodes, 500)
          ? "is-invalid"
          : ""
          }`}
        {...bind.select("zipCodes")}
        placeholder="Comma separated ZIP codes e.g. 37067,37027"
      />
      {!validateZipCodes(common.zipCodes, ["zip", "zip+4"]) && (
        <div className="offset-3 invalid-feedback">
          Contains invalid Zip code. Only five digit numbers separated by commas
          permitted.
        </div>
      )}
      {!underZipLimit(common.zipCodes, 500) && (
        <div className="offset-3 invalid-feedback">
          Limited to 500 ZIP codes.
        </div>
      )}
    </div>
  );
}

export function CBSAInput({ common, set }) {
  return (
    <div className="form-group row">
      <OverlayTrigger
        key="cbsa"
        placement="left"
        overlay={<Tooltip>Provide a CBSA to define your market</Tooltip>}
      >
        <label htmlFor="cbsa" className="col-3 col-form-label">
          CBSA
        </label>
      </OverlayTrigger>
      <Dropdown>
        <Dropdown.Toggle as={CustomToggle} id="dropdown-custom-components">
          {common?.cbsaSelection
            ? `${common?.cbsaSelection?.code} - ${common?.cbsaSelection?.name}`
            : "Select CBSA"}
        </Dropdown.Toggle>

        <Dropdown.Menu as={CustomMenu}>
          {cbsa.map((code, idx) => (
            <Dropdown.Item
              eventKey={idx}
              key={code.code}
              onClick={() => set("cbsaSelection", code)}
            >
              {code.code} - {code.name}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}

export function StateSelect({ common, set }) {
  return (
    <div className="form-group row">
      <OverlayTrigger
        key="state"
        placement="left"
        overlay={<Tooltip>Provide a state to define your market</Tooltip>}
      >
        <label htmlFor="state" className="col-3 col-form-label">
          State
        </label>
      </OverlayTrigger>
      <Dropdown>
        <Dropdown.Toggle as={CustomToggle} id="dropdown-custom-components">
          {common.stateSelection
            ? `${common.stateSelection?.value} - ${common.stateSelection?.name}`
            : "Select State"}
        </Dropdown.Toggle>

        <Dropdown.Menu as={CustomMenu}>
          {states.map((stateItem, idx) => (
            <Dropdown.Item
              eventKey={idx}
              key={stateItem.value}
              onClick={() => set("stateSelection", stateItem)}
            >
              {stateItem.value} - {stateItem.name}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}

export function PatientZip3Input({ common, bind }) {
  return (
    <div className="form-group row">
      <OverlayTrigger
        key="patientZipCodes"
        placement="left"
        overlay={
          <Tooltip>
            Provide a comma separated list of three digit zip codes to define
            your market
          </Tooltip>
        }
      >
        <label htmlFor="locations" className="col-3 col-form-label">
          Patient ZIP3 codes
        </label>
      </OverlayTrigger>
      <textarea
        className={`form-control col-6 ${!validateZipCodes(common.patientZip3s, ["zip3"]) ||
          !underZipLimit(common.patientZip3s, 20)
          ? "is-invalid"
          : ""
          }`}
        {...bind.text("patientZip3s")}
        placeholder="Comma separated ZIP3 codes e.g. 123,124"
      />
      {!validateZipCodes(common.patientZip3s, ["zip3"]) && (
        <div className="offset-3 invalid-feedback">
          Contains invalid Zip3 code. Only three digit numbers separated by
          commas permitted.
        </div>
      )}
      {!underZipLimit(common.patientZip3s, 20) && (
        <div className="offset-3 invalid-feedback">
          Limited to 20 ZIP3 codes.
        </div>
      )}
    </div>
  );
}
 
export function DateRange({ common, bind, label, overlayText, second }) {
  const getDateFieldName = (field) => second ? `${field}2` : field;

  const priorParams = common?.priorQueryParams?.definition?.parameters || {};

  const priorStart = getPriorDate(priorParams, getDateFieldName("startDate"));
  const priorEnd   = getPriorDate(priorParams, getDateFieldName("endDate"));

  const isStartDateWarning = priorStart && priorStart === formatDate(common[getDateFieldName("startDate")]);
  const isEndDateWarning   = priorEnd   && priorEnd   === formatDate(common[getDateFieldName("endDate")]);

  return (
    <div className="form-group row">
      <OverlayTrigger
        key="dateRange"
        placement="left"
        overlay={<Tooltip>{overlayText}</Tooltip>}
      >
        <label className="col-form-label col-3">{label}</label>
      </OverlayTrigger>
      <div className="col-3 pl-0">
        <DatePicker
          name="start"
          showYearDropdown
          maxDate={common[getDateFieldName("endDate")]}
          {...bind.date(getDateFieldName("startDate"))}
          className={`form-control ${common.priorQueryParams &&
            isStartDateWarning
            ? "is-warning"
            : null
            }`}
          placeholderText="Set Start Date"
        />
        {isStartDateWarning && (
          <div
            className="invalid-feedback text-orange"
            style={{ display: "block" }}
          >
            Start date is the same as original start date.
          </div>
        )}
      </div>
      <div className="col-3 pl-0">
        <DatePicker
          name="end"
          showYearDropdown
          minDate={common[getDateFieldName("startDate")]}
          maxDate={new Date()}
          {...bind.date(getDateFieldName("endDate"))}
          className={`form-control ${common.priorQueryParams &&
            isEndDateWarning
            ? "is-warning"
            : null
            }`}
          placeholderText="Set End Date"
        />
        {isEndDateWarning && (
          <div
            className="invalid-feedback text-orange"
            style={{ display: "block" }}
          >
            End date is the same as original end date.
          </div>
        )}
      </div>
    </div>
  );
}

export function VisualizationToggle({ common, bind }) {
  return (
    <div className="form-group row">
      <OverlayTrigger
        key="canBeVisualized"
        placement="left"
        overlay={
          <Tooltip>
            A Dashboard wil be provided with your query, display your custom
            queried data. <br />
            Including a visualization will disable custom column selections
          </Tooltip>
        }
      >
        <label className="col-3 col-form-label">
          Include a visualization with query output
        </label>
      </OverlayTrigger>
      <div className="col-9 d-flex align-items-center">
        <input
          name="canBeVisualized"
          type="checkbox"
          className="form-check"
          style={{ width: "20px", height: "20px" }}
          checked={common.canBeVisualized}
          {...bind.checkbox("canBeVisualized")}
        />
      </div>
    </div>
  );
}

export function NPIsInput({ common, bind, label, overlayText, second }) {
  const isValid = second ? validateNpis(common.npis2) : validateNpis(common.npis);
  const isUnderLimit = second ? underNpiLimit(common.npis2) : underNpiLimit(common.npis);

  return (
    <div className="form-group row">
      <OverlayTrigger
        key="physicianNpis"
        placement="left"
        overlay={
          <Tooltip>
            {overlayText ? overlayText : "Enter the NPIs of the physician's patients you want to track"}
          </Tooltip>
        }
      >
        <label className="col-3 col-form-label">{label ? label : "Physician NPIs"}</label>
      </OverlayTrigger>
      <div className="col-9  pl-0">
        <textarea
          className={`form-control ${!isValid || !isUnderLimit
            ? "is-invalid"
            : ""
            }`}
          {...bind.text(second ? "npis2" : "npis")}
          placeholder="Comma separated NPIs e.g. 1234567890, 1234567891"
        />
        {!isValid && (
          <div className="invalid-feedback">
            Contains invalid NPI. Only ten digit numbers separated by commas
            permitted.
          </div>
        )}
        {!isUnderLimit && (
          <div className="invalid-feedback">Limited to 1000 NPIs.</div>
        )}
      </div>
    </div>
  );
}

export function MeasureTimeFrameSelect({ bind, categoryId }) {
  return (
    <div className="form-group row">
      <OverlayTrigger
        key="measureTimeFrame"
        placement="left"
        overlay={
          <Tooltip>
            Track those patients for 30, 60, or 90 days BEFORE (-) or AFTER (+) an
            encounter with the selected physicians during the defined capture
            date
          </Tooltip>
        }
      >
        <label className="col-3 col-form-label">Measure Timeframe</label>
      </OverlayTrigger>
      <div className="col-9  pl-0">
        <div className="row">
          {categoryId === populationTrackerIDs.categoryId && (
            <div className="col-6">
              <select
                className="form-control"
                {...bind.select("timeFrameDirection")}
              >
                <option value="">+ or -</option>
                <option value="+">
                  +
                </option>
                <option value="-">
                  -
                </option>
              </select>
            </div>
          )}
          <div className="col-6">
            <select className="form-control" {...bind.select("timeFrameDays")}>
              <option value="">Select 30, 60, 90 or 180 days</option>
              <option value="30">
                30 days
              </option>
              <option value="60">
                60 days
              </option>
              <option value="90">
                90 days
              </option>
              <option value="180">
                180 days
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CodeTypeSelect({ common, bind, categoryId, label, overlayText }) {
  return (
    <div className="form-group row">
      <OverlayTrigger
        key="codetype"
        placement="left"
        overlay={<Tooltip>{overlayText ? overlayText : "Output code type"}</Tooltip>}
      >
        <label className="col-3 col-form-label">{label ? label : "Code Type"}</label>
      </OverlayTrigger>
      <select className="col-3 form-control" {...bind.select("codeType")}>
        <option value="">Select Code Type</option>
        {(categoryId === populationTrackerIDs.categoryId ||
          categoryId === shareOfCareIDs.categoryId
        ) && (
            <>
              <option value="diag_code">Diagnosis Code</option>
              <option value="procedurecode">Procedure Code</option>
            </>
          )}
        {(categoryId === shareOfCareIDs.categoryId &&
          common.providerType === 'organization'
        ) && (
            <option value="msdrg">MS-DRG</option>
          )}
        {categoryId === clinicalMarketShareIDs.categoryId && (
          <>
            {common.payerMix === 'commercial' && (
              <>
                <option value="procedurecode">HCPCS/CPT</option>
                <option value="diag_code">ICD-10-CM</option>
              </>
            )}
            {common.payerMix === 'medicare' && (
              <>
                <option value="hcpcs_cd">HCPCS</option>
                <option value="clm_drg_cd">Claim DRG</option>
                <option value="prncpal_dgns_cd">Principal Diagnosis</option>
              </>
            )}
          </>
        )}
        {categoryId === clinicalVolumesIDs.categoryId && (
          <>
            <option value="procedurecode">CPT/HCPCS</option>
            <option value="diag_code">ICD10DX</option>
            <option value="msdrg">MSDRG</option>
          </>
        )}
      </select>
    </div>
  );
}

export function CodeInput({ common, bind }) {
  return (
    <div className="form-group row">
      <OverlayTrigger
        key="codeInput"
        placement="left"
        overlay={<Tooltip>Up to 20 code(s) for a given timeframe</Tooltip>}
      >
        <label className="col-3 col-form-label">Codes to Measure</label>
      </OverlayTrigger>
      <div className="col-6 pl-0 pr-0">
        <textarea
          className={`form-control ${!underCodesLimit(common.codeInput) ? 'is-invalid' : ''
            }`}
          {...bind.text('codeInput')}
          placeholder="(up to 20 comma separated codes)"
        />
        {!underCodesLimit(common.codeInput) && (
          <div className="invalid-feedback">Limited to 20 codes.</div>
        )}
      </div>
    </div>
  )
}

export function CountiesSelect({ common, queryObj, set }) {
  return (
    <div className="form-group row">
      <OverlayTrigger
        key="counties"
        placement="left"
        overlay={
          <Tooltip>Provide up to 10 counties to define your market</Tooltip>
        }
      >
        <label htmlFor="counties" className="col-3 col-form-label">
          Counties
        </label>
      </OverlayTrigger>
      <div className="col-6 pl-0 pr-0">
        <Dropdown>
          <Dropdown.Toggle as={CustomToggle} id="dropdown-custom-components">
            {common.countiesSelection && common.countiesSelection.length > 0
              ? joinCounties(queryObj.parameters.fips, false)
              : "Select Counties"}
          </Dropdown.Toggle>

          <Dropdown.Menu
            selectedCounties={common.countiesSelection}
            setSelectedCounties={newArray => set("countiesSelection", newArray)}
            as={CustomMenuCounties}
          >
            {counties.map((county, idx) => (
              <Dropdown.Item eventKey={idx} key={county.code}>
                {county}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
        {common.countiesSelection && common.countiesSelection.length > 10 && (
          <div
            style={{
              width: "100%",
              marginTop: "0.25rem",
              fontSize: "90%",
              color: "#dc3545"
            }}
          >
            Max 10 counties allowed. Please limit your selections to enable
            submission
          </div>
        )}
      </div>
    </div>
  );
}

export function CheckboxInput({ item, common, selectors, setOutput }) {
  const ctx = { common, selectors }
  const label =
    typeof item.label === "function"
      ? item.label({ common, selectors })
      : item.label;

    let checked;
    if (typeof item.checked !== 'undefined') {
      checked = typeof item.checked === 'function' ? item.checked({ common, selectors }) : !!item.checked;
    } else if (item.forceChecked) {
      checked = true;
    }

  const disabled =
    typeof item.disabled === "function"
      ? item.disabled({ common, selectors })
      : item.disabled;

  const visible =
    typeof item.visible === "function"
      ? item.visible({ common, selectors })
      : item.visible ?? true;

  if (!visible) return null;
  

  const handleChange = (e) => {
    const isChecked = e.target.checked;
    if (item.forceChecked || disabled) return;
    if (typeof item.onToggle === 'function') {
      item.onToggle({ ctx, setOutput }, isChecked);
    } else {
      setOutput(item.name, isChecked);
    }
  };



  return (
    <div key={item.name} className="form-check form-check-inline">
      <input
        name={item.name}
        type="checkbox"
        className="form-check-input"
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
      />
      <label className="form-check-label">{label}</label>
    </div>
  );
}